/**
 * DeviceConfigModal — Sprint 4.4.
 *
 * Modal de configuração remota do device com 5 abas:
 *   1. Rede        (SSID/senha Wi-Fi, DHCP/static, IP/mask/gw/dns)
 *   2. Gerência    (admin password, hostname, timezone, NTP, LED)
 *   3. Watchdog    (enable/ping target/intervalo/timeout/reboot after)
 *   4. Agenda      (schedule entries com dias da semana + on/off times)
 *   5. Avançado    (placeholder: safe mode, intervalo HB, RSSI threshold, etc)
 *
 * Cada aba tem botão "Aplicar" próprio que enfileira UM comando dedicado.
 * Não há "Aplicar tudo" — minimiza risco de fazer mudanças não-relacionadas
 * de uma vez.
 *
 * Senhas:
 *   - Input type='password' por padrão, toggle 👁 para revelar antes do submit
 *   - Backend nunca devolve senha real em GET (mascarada com ••••••••)
 *   - Campos NUNCA pré-preenchidos com valor anterior
 *
 * Confirmação dupla (P1):
 *   - Aba Rede: avisa que mudança pode desconectar device → window.confirm
 *
 * Histórico (Recent):
 *   - Cada aba mostra os últimos N comandos do tipo correspondente
 *     com status (pending/delivered/acked/failed) e timestamp
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import {
  configAdvanced,
  configManagement,
  configNetwork,
  configSchedule,
  configWatchdog,
  configWifi,
  getDeviceConfigSnapshot,
  listDeviceConfigRecent,
  listScheduleEvents,
} from "../../lib/api.js";
import { t } from "../../i18n/index.js";
import { useCriticalConfirm } from "../ConfirmCriticalAction.jsx";
import ScheduleEditor, {
  validateSchedule,
} from "../schedule/ScheduleEditor.jsx";
import {
  buildConfigSchedulePayload,
  validateScheduleForCapability,
  getSchedulerCapability,
  resolveActiveSchedule,
} from "../../lib/scheduleCore.js";
import SchedulePreviewModal from "../schedule/SchedulePreviewModal.jsx";
import ScheduleSourcePicker from "../schedule/ScheduleSourcePicker.jsx";
import SchedulePresetsBox from "../schedule/SchedulePresetsBox.jsx";
import ScheduleV2Card from "./ScheduleV2Card.jsx";

const TABS = [
  { id: "wifi",       label: t("tabs.wifi"),       icon: "📶" },
  { id: "management", label: t("tabs.management"), icon: "⚙" },
  { id: "watchdog",   label: t("tabs.watchdog"),   icon: "🐕" },
  { id: "schedule",   label: t("tabs.schedule"),   icon: "🗓" },
  { id: "advanced",   label: t("tabs.advanced"),   icon: "🛠" },
];

// Sprint 4.4.2 — estilos por estado de sync (siglas técnicas mantidas em caixa alta)
const STATE_BADGES = {
  synced:   { label: t("status.synced").toUpperCase(),   cls: "bg-noc-ok/10 text-noc-ok border-noc-ok/30" },
  pending:  { label: t("status.pending").toUpperCase(),  cls: "bg-noc-warn/10 text-noc-warn border-noc-warn/30" },
  outdated: { label: t("status.outdated").toUpperCase(), cls: "bg-noc-orange/10 text-noc-orange border-noc-orange/30" },
  unknown:  { label: t("status.unknown").toUpperCase(),  cls: "bg-noc-bg/60 text-noc-muted border-noc-border" },
};

export default function DeviceConfigModal({ open, deviceId, onClose }) {
  const [tab, setTab] = useState("wifi");
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  // Sprint 4.4.2 — snapshot atual do device
  const [snapshot, setSnapshot] = useState(null);
  const [snapState, setSnapState] = useState("unknown");
  const [snapSyncedAt, setSnapSyncedAt] = useState(null);
  const [snapRevision, setSnapRevision] = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(false);

  // Reload histórico
  const reloadRecent = async () => {
    if (!deviceId) return;
    setLoadingRecent(true);
    try {
      const data = await listDeviceConfigRecent(deviceId, { limit: 20 });
      setRecent(data.commands || []);
    } catch (e) {
      console.warn("config/recent falhou:", e);
    } finally {
      setLoadingRecent(false);
    }
  };

  // Sprint 4.4.2 — carrega snapshot do device
  const reloadSnapshot = async () => {
    if (!deviceId) return;
    setLoadingSnap(true);
    try {
      const data = await getDeviceConfigSnapshot(deviceId);
      setSnapshot(data.last_config_snapshot);
      setSnapState(data.state || "unknown");
      setSnapSyncedAt(data.config_synced_at);
      setSnapRevision(data.config_revision);
    } catch (e) {
      console.warn("GET /config falhou:", e);
      setSnapshot(null);
      setSnapState("unknown");
    } finally {
      setLoadingSnap(false);
    }
  };

  useEffect(() => {
    if (!open || !deviceId) return;
    setTab("wifi");
    reloadRecent();
    reloadSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deviceId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const sharedProps = {
    deviceId,
    recent,
    snapshot,
    snapState,
    snapSyncedAt,
    snapRevision,
    onApplied: async () => {
      await reloadRecent();
      // Aguarda um pouco para o backend processar — refresh do snapshot
      // virá quando device enviar próximo hb com snapshot atualizado.
      // Por ora, só atualiza recent + pending count.
      await reloadSnapshot();
    },
  };

  const badge = STATE_BADGES[snapState] || STATE_BADGES.unknown;

  // Robustez/simetria (MAP-UX): portaliza p/ document.body como
  // ConfirmCriticalAction/BulkRelayRebootModal. Disparado de dentro da árvore do
  // mapa, que em fullscreen vira .ipwd-fleet-focus (position:fixed). Hoje pinta
  // certo, mas se alguém adicionar transform/filter no foco este inner fixed
  // seria re-clampado ao box z-1500; no body fica imune. z-[2000] segue acima do
  // mapa e abaixo da confirmação crítica (z-3000/4000).
  return createPortal(
    <div
      className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-3xl flex flex-col"
           style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <h3 className="text-sm font-semibold text-noc-accent flex items-center gap-2">
            ⚙ Configurar Device · <span className="font-mono text-noc-accent">{deviceId}</span>
            {/* Sprint 4.4.2 — badge state */}
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${badge.cls}`}>
              {badge.label}
            </span>
            {snapRevision !== null && (
              <span className="text-[10px] text-noc-muted/70 font-mono">rev {snapRevision}</span>
            )}
          </h3>
          <button type="button" onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none">×</button>
        </div>

        {/* Sub-header info */}
        {(snapSyncedAt || loadingSnap) && (
          <div className="px-4 py-1.5 text-[10px] text-noc-muted/80 bg-noc-bg/30 border-b border-noc-border/60 flex items-center gap-3">
            {loadingSnap ? (
              <span>carregando snapshot…</span>
            ) : snapSyncedAt ? (
              <span>último sync: {fmtRel(snapSyncedAt)}</span>
            ) : null}
            {snapState === "unknown" && (
              <span className="text-noc-warn">⚠ {t("device.firmwareOldWarning")}</span>
            )}
            {snapState === "pending" && (
              <span className="text-noc-warn">⏳ {t("device.pendingConfig")}</span>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-noc-border bg-noc-bg/30">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-noc-accent/30 text-noc-accent bg-noc-accent/10"
                  : "border-transparent text-noc-muted hover:text-noc-text"
              }`}
            >
              <span className="mr-1">{t.icon}</span> {t.label}
            </button>
          ))}
          <div className="flex-1" />
          <div className="px-3 py-2 text-[10px] text-noc-muted/70 flex items-center gap-1">
            {loadingRecent && <span>recarregando…</span>}
            <button
              type="button"
              onClick={reloadRecent}
              title="Recarregar histórico"
              className="hover:text-noc-text"
            >🔄</button>
          </div>
        </div>

        {/* Aba conteúdo + histórico (split) */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {/* Formulário (2/3) */}
            <div className="md:col-span-2 p-4">
              {tab === "wifi"       && <WifiTab {...sharedProps} />}
              {tab === "management" && <ManagementTab {...sharedProps} />}
              {tab === "watchdog"   && <WatchdogTab {...sharedProps} />}
              {tab === "schedule"   && <ScheduleTab {...sharedProps} />}
              {tab === "advanced"   && <AdvancedTab {...sharedProps} />}
            </div>
            {/* Histórico (1/3) */}
            <div className="md:col-span-1 p-4 border-t md:border-t-0 md:border-l border-noc-border bg-noc-bg/40">
              {tab === "advanced" ? (
                /* Hotfix s442.1 — aba Avançado mostra TODOS os tipos config */
                <RecentList recent={recent} filterType={null} />
              ) : (
                <RecentList recent={recent} filterType={`config_${tab}`} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}


// ============================================================================
// Histórico
// ============================================================================

const STATUS_COLORS = {
  pending:   "text-noc-warn",
  delivered: "text-noc-accent",
  acked:     "text-noc-ok",
  failed:    "text-noc-err",
  expired:   "text-noc-muted",
};

function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s atrás`;
  if (s < 3600) return `${Math.floor(s / 60)}min atrás`;
  if (s < 86400) return `${Math.floor(s / 3600)}h atrás`;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function RecentList({ recent, filterType }) {
  const filtered = (recent || []).filter((c) => !filterType || c.type === filterType);

  // Hotfix s442.1 — quando filterType é null (aba Avançado), label vira
  // "Histórico — config" (todos os tipos) e cada card destaca o tipo
  // específico (wifi/network/management/watchdog/schedule).
  const sectionLabel = filterType
    ? filterType.replace("config_", "")
    : "config";

  return (
    <div className="space-y-1">
      <div className="text-[10px] text-noc-muted/70 uppercase tracking-wide mb-2">
        Histórico — {sectionLabel}
      </div>
      {filtered.length === 0 ? (
        <div className="text-[11px] text-noc-muted/60 italic">
          Nenhum comando enviado ainda.
        </div>
      ) : (
        filtered.slice(0, 10).map((c) => {
          // Hotfix s442.1 — extrai tipo curto p/ exibir no card quando
          // filterType=null (aba Avançado). Ex: config_management → management
          const shortType = (c.type || "").replace("config_", "");
          return (
            <div key={c.id} className="bg-noc-bg/60 border border-noc-border/40 rounded px-2 py-1.5 text-[10px]">
              <div className="flex items-center justify-between gap-2">
                <span className={`font-mono ${STATUS_COLORS[c.status] || "text-noc-muted"}`}>
                  ● {c.status}
                </span>
                {!filterType && (
                  <span className="font-mono text-noc-accent text-[10px]">
                    {shortType}
                  </span>
                )}
                <span className="text-noc-muted/70">{fmtRel(c.created_at)}</span>
              </div>
              {c.payload_sanitized && Object.keys(c.payload_sanitized).length > 0 && (
                <div className="mt-1 font-mono text-[9px] text-noc-muted/80 max-h-16 overflow-y-auto">
                  {Object.entries(c.payload_sanitized).slice(0, 6).map(([k, v]) => (
                    <div key={k} className="truncate">
                      {k}: <span className="text-noc-text/80">{
                        typeof v === "object" ? JSON.stringify(v) : String(v ?? "")
                      }</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}


// ============================================================================
// Ações compartilhadas
// ============================================================================

function ApplyButton({ busy, onClick, label = "Aplicar", warn = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`text-xs px-3 py-1.5 rounded ${
        warn
          ? "bg-noc-warn/10 hover:bg-noc-warn/10 text-noc-warn"
          : "bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold"
      } disabled:opacity-50`}
    >
      {busy ? "Enviando…" : label}
    </button>
  );
}

function ErrorBox({ err, onDismiss }) {
  if (!err) return null;
  return (
    <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 flex items-start gap-2">
      <span className="flex-1">{err}</span>
      <button type="button" onClick={onDismiss} className="text-noc-err hover:text-noc-err">×</button>
    </div>
  );
}

function SuccessBadge({ shown }) {
  if (!shown) return null;
  return (
    <div className="text-xs text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2">
      ✓ Comando enfileirado. Vai chegar ao device no próximo heartbeat.
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, name }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex gap-1">
      <input
        type={show ? "text" : "password"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="new-password"
        name={name}
        className="flex-1 bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs font-mono"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        title={show ? "Ocultar" : "Mostrar"}
        className="text-xs px-2 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text"
      >
        {show ? "🙈" : "👁"}
      </button>
    </div>
  );
}

// Sprint 4.4.2 — mostra "Atual: X → Novo: Y" quando valor digitado difere do snapshot.
// Permanece silencioso quando current é null/undefined (sem snapshot ainda).
function DiffHint({ current, next, format }) {
  if (current === null || current === undefined || current === "") return null;
  const fmt = format || ((v) => String(v));
  const curStr = fmt(current);
  const nextStr = fmt(next);
  if (curStr === nextStr) return null;
  return (
    <div className="text-[10px] text-noc-accent mt-0.5 font-mono">
      Atual: <span className="text-noc-muted">{curStr || "—"}</span>
      <span className="mx-1">→</span>
      <span className="text-noc-accent">{nextStr || "—"}</span>
    </div>
  );
}


// ============================================================================
// ABA REDE (Wi-Fi + IP)
// ============================================================================

function WifiTab({ deviceId, snapshot, onApplied }) {
  // S3.10C1 — confirmação forte de config crítica (hook no escopo do tab).
  const confirmCritical = useCriticalConfirm();
  // Sprint 4.4.2 — pré-popula com snapshot (sem senha, claro)
  const cur = snapshot?.wifi || {};
  const [ssid, setSsid] = useState(cur.ssid || "");
  const [password, setPassword] = useState("");
  const [hidden, setHidden] = useState(false);
  const [dhcp, setDhcp] = useState(cur.dhcp !== false);  // default true se ausente
  const [ip, setIp] = useState(cur.ip || "");
  const [mask, setMask] = useState(cur.mask || "255.255.255.0");
  const [gateway, setGateway] = useState(cur.gateway || "");
  const [dns1, setDns1] = useState(cur.dns1 || "8.8.8.8");
  const [dns2, setDns2] = useState(cur.dns2 || "1.1.1.1");

  // Re-sincroniza quando snapshot chega depois de o tab montar
  useEffect(() => {
    if (!snapshot?.wifi) return;
    const w = snapshot.wifi;
    setSsid(w.ssid || "");
    setDhcp(w.dhcp !== false);
    if (w.ip) setIp(w.ip);
    if (w.mask) setMask(w.mask);
    if (w.gateway) setGateway(w.gateway);
    if (w.dns1) setDns1(w.dns1);
    if (w.dns2) setDns2(w.dns2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.wifi?.ssid, snapshot?.wifi?.dhcp]);

  const [busyWifi, setBusyWifi] = useState(false);
  const [busyNet, setBusyNet] = useState(false);
  const [errWifi, setErrWifi] = useState(null);
  const [errNet, setErrNet] = useState(null);
  const [okWifi, setOkWifi] = useState(false);
  const [okNet, setOkNet] = useState(false);

  const submitWifi = async () => {
    if (!ssid.trim()) {
      setErrWifi("SSID obrigatório.");
      return;
    }
    const secret = await confirmCritical({
      actionLabel: "Aplicar configuração de Wi-Fi",
      target: { kind: "single", deviceId },
      impact:
        `Aplicar SSID "${ssid}" pode DESCONECTAR o device imediatamente se a ` +
        "senha estiver errada ou a rede não estiver acessível. Ele só reaparece " +
        "no NOC quando reconectar.",
    });
    if (secret == null) return;
    setBusyWifi(true);
    setErrWifi(null);
    setOkWifi(false);
    try {
      await configWifi(deviceId, { ssid: ssid.trim(), password: password || null, hidden }, { confirm: secret });
      setOkWifi(true);
      setPassword(""); // limpa senha após submit (segurança)
      onApplied?.();
      setTimeout(() => setOkWifi(false), 5000);
    } catch (e) {
      setErrWifi(e?.message || "Erro ao aplicar Wi-Fi");
    } finally {
      setBusyWifi(false);
    }
  };

  const submitNet = async () => {
    const secret = await confirmCritical({
      actionLabel: "Aplicar configuração de rede",
      target: { kind: "single", deviceId },
      impact:
        "Alterar a rede pode tornar o device INALCANÇÁVEL se o IP/gateway " +
        "estiver fora da subnet acessível.",
    });
    if (secret == null) return;
    setBusyNet(true);
    setErrNet(null);
    setOkNet(false);
    try {
      const body = dhcp
        ? { dhcp: true }
        : { dhcp: false, ip: ip || null, mask: mask || null, gateway: gateway || null,
            dns1: dns1 || null, dns2: dns2 || null };
      await configNetwork(deviceId, body, { confirm: secret });
      setOkNet(true);
      onApplied?.();
      setTimeout(() => setOkNet(false), 5000);
    } catch (e) {
      setErrNet(e?.message || "Erro ao aplicar rede");
    } finally {
      setBusyNet(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Wi-Fi */}
      <fieldset className="border border-noc-border rounded p-3 space-y-2">
        <legend className="text-xs font-semibold text-noc-accent px-1">Wi-Fi</legend>

        <div className="text-[10px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
          ⚠ Mudança de SSID/senha pode desconectar o device. Apenas reconectará na nova rede.
        </div>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">SSID</span>
          <input value={ssid} onChange={(e) => setSsid(e.target.value)}
            placeholder="Fibra-Net-2G" maxLength={32}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
          <DiffHint current={cur.ssid} next={ssid} />
        </label>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Senha (vazio = rede aberta)</span>
          <PasswordInput value={password} onChange={setPassword}
            placeholder="••••••••" name="wifi_password" />
        </label>

        <label className="flex items-center gap-2 text-xs text-noc-muted">
          <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
          SSID oculto (hidden)
        </label>

        <ErrorBox err={errWifi} onDismiss={() => setErrWifi(null)} />
        <SuccessBadge shown={okWifi} />

        <div className="flex justify-end">
          <ApplyButton busy={busyWifi} onClick={submitWifi} label="Aplicar Wi-Fi" warn />
        </div>
      </fieldset>

      {/* IP */}
      <fieldset className="border border-noc-border rounded p-3 space-y-2">
        <legend className="text-xs font-semibold text-noc-accent px-1">Endereço IP</legend>

        <div className="flex gap-3 text-xs">
          <label className="flex items-center gap-1">
            <input type="radio" name="mode" checked={dhcp} onChange={() => setDhcp(true)} />
            DHCP
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" name="mode" checked={!dhcp} onChange={() => setDhcp(false)} />
            IP estático
          </label>
        </div>
        {cur.dhcp !== undefined && cur.dhcp !== dhcp && (
          <div className="text-[10px] text-noc-accent font-mono">
            Atual: <span className="text-noc-muted">{cur.dhcp ? "DHCP" : "Estático"}</span>
            <span className="mx-1">→</span>
            <span className="text-noc-accent">{dhcp ? "DHCP" : "Estático"}</span>
          </div>
        )}

        {!dhcp && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="space-y-0.5">
              <span className="text-noc-muted">IP</span>
              <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="10.0.0.5"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.ip} next={ip} />
            </label>
            <label className="space-y-0.5">
              <span className="text-noc-muted">Máscara</span>
              <input value={mask} onChange={(e) => setMask(e.target.value)} placeholder="255.255.255.0"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.mask} next={mask} />
            </label>
            <label className="space-y-0.5">
              <span className="text-noc-muted">Gateway</span>
              <input value={gateway} onChange={(e) => setGateway(e.target.value)} placeholder="10.0.0.1"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.gateway} next={gateway} />
            </label>
            <label className="space-y-0.5">
              <span className="text-noc-muted">DNS1</span>
              <input value={dns1} onChange={(e) => setDns1(e.target.value)} placeholder="8.8.8.8"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.dns1} next={dns1} />
            </label>
            <label className="space-y-0.5 col-span-2">
              <span className="text-noc-muted">DNS2 (opcional)</span>
              <input value={dns2} onChange={(e) => setDns2(e.target.value)} placeholder="1.1.1.1"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.dns2} next={dns2} />
            </label>
          </div>
        )}

        <ErrorBox err={errNet} onDismiss={() => setErrNet(null)} />
        <SuccessBadge shown={okNet} />

        <div className="flex justify-end">
          <ApplyButton busy={busyNet} onClick={submitNet} label="Aplicar IP" warn />
        </div>
      </fieldset>
    </div>
  );
}


// ============================================================================
// ABA GERÊNCIA
// ============================================================================

function ManagementTab({ deviceId, snapshot, onApplied }) {
  // S3.10C1 — confirmação forte de config crítica (hook no escopo do tab).
  const confirmCritical = useCriticalConfirm();
  const cur = snapshot?.management || {};
  // Sprint 4.6.4 — snapshot do firmware ainda emite ntp/led (chaves curtas),
  // mas backend ConfigManagementIn aceita SÓ ntp_server/led_status no envio.
  // Aqui fundimos: lemos ambas as chaves do snapshot, mas SEMPRE enviamos
  // os nomes oficiais no submit.
  const curNtp = cur.ntp_server ?? cur.ntp ?? "";
  const curLed = (typeof cur.led_status === "boolean") ? cur.led_status
               : (typeof cur.led === "boolean") ? cur.led
               : undefined;

  const [adminPassword, setAdminPassword] = useState("");
  const [hostname, setHostname] = useState(cur.hostname || "");
  const [timezone, setTimezone] = useState(cur.timezone || "");
  const [ntpServer, setNtpServer] = useState(curNtp);
  const [ledStatus, setLedStatus] = useState(null);  // null = não muda

  // Re-sync quando snapshot chega
  useEffect(() => {
    if (!snapshot?.management) return;
    const m = snapshot.management;
    if (m.hostname) setHostname(m.hostname);
    if (m.timezone) setTimezone(m.timezone);
    // Sprint 4.6.4 — aceita ambas as chaves do snapshot (firmware emite 'ntp')
    const next = m.ntp_server ?? m.ntp;
    if (next) setNtpServer(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.management?.hostname]);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);

  const submit = async () => {
    const body = {};
    if (adminPassword) body.admin_password = adminPassword;
    if (hostname.trim()) body.hostname = hostname.trim();
    if (timezone.trim()) body.timezone = timezone.trim();
    if (ntpServer.trim()) body.ntp_server = ntpServer.trim();
    if (ledStatus !== null) body.led_status = ledStatus;

    if (Object.keys(body).length === 0) {
      setErr("Preencha ao menos um campo.");
      return;
    }
    const secret = await confirmCritical({
      actionLabel: "Aplicar configuração gerencial",
      target: { kind: "single", deviceId },
      impact:
        "Pode alterar senha de admin, hostname, timezone, NTP e LED do dispositivo.",
    });
    if (secret == null) return;
    setBusy(true);
    setErr(null);
    setOk(false);
    try {
      await configManagement(deviceId, body, { confirm: secret });
      setOk(true);
      setAdminPassword(""); // limpa senha após submit
      onApplied?.();
      setTimeout(() => setOk(false), 5000);
    } catch (e) {
      setErr(e?.message || "Erro ao aplicar gerência");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-[10px] text-noc-muted/70">
        Apenas campos preenchidos serão alterados. Campos vazios = sem mudança.
      </div>

      <label className="block text-xs space-y-1">
        <span className="text-noc-muted">Senha admin local</span>
        <PasswordInput value={adminPassword} onChange={setAdminPassword}
          placeholder="••••••••" name="admin_password" />
      </label>

      <label className="block text-xs space-y-1">
        <span className="text-noc-muted">Hostname (RFC 952: letras/dígitos/hífen)</span>
        <input value={hostname} onChange={(e) => setHostname(e.target.value)}
          placeholder="sonoff-recepcao" maxLength={63}
          className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
        <DiffHint current={cur.hostname} next={hostname} />
      </label>

      <label className="block text-xs space-y-1">
        <span className="text-noc-muted">Timezone (TZ format)</span>
        <input value={timezone} onChange={(e) => setTimezone(e.target.value)}
          placeholder="America/Bahia" maxLength={64}
          className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
        <DiffHint current={cur.timezone} next={timezone} />
      </label>

      <label className="block text-xs space-y-1">
        <span className="text-noc-muted">NTP server</span>
        <input value={ntpServer} onChange={(e) => setNtpServer(e.target.value)}
          placeholder="pool.ntp.org" maxLength={128}
          className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
        <DiffHint current={curNtp} next={ntpServer} />
      </label>

      <div className="text-xs space-y-1">
        <span className="text-noc-muted">LED de status</span>
        <div className="flex gap-2">
          <button type="button" onClick={() => setLedStatus(null)}
            className={`px-2 py-1 rounded text-[10px] border ${ledStatus === null ? "border-noc-accent/30 text-noc-accent" : "border-noc-border text-noc-muted"}`}>
            não muda
          </button>
          <button type="button" onClick={() => setLedStatus(true)}
            className={`px-2 py-1 rounded text-[10px] border ${ledStatus === true ? "border-noc-ok/30 text-noc-ok" : "border-noc-border text-noc-muted"}`}>
            ligado
          </button>
          <button type="button" onClick={() => setLedStatus(false)}
            className={`px-2 py-1 rounded text-[10px] border ${ledStatus === false ? "border-noc-err/30 text-noc-err" : "border-noc-border text-noc-muted"}`}>
            desligado
          </button>
        </div>
        {curLed !== undefined && ledStatus !== null && curLed !== ledStatus && (
          <div className="text-[10px] text-noc-accent font-mono">
            Atual: <span className="text-noc-muted">{curLed ? "ligado" : "desligado"}</span>
            <span className="mx-1">→</span>
            <span className="text-noc-accent">{ledStatus ? "ligado" : "desligado"}</span>
          </div>
        )}
      </div>

      <ErrorBox err={err} onDismiss={() => setErr(null)} />
      <SuccessBadge shown={ok} />

      <div className="flex justify-end">
        <ApplyButton busy={busy} onClick={submit} />
      </div>
    </div>
  );
}


// ============================================================================
// ABA WATCHDOG
// ============================================================================

function WatchdogTab({ deviceId, snapshot, onApplied }) {
  // S3.10C1 — confirmação forte de config crítica (hook no escopo do tab).
  const confirmCritical = useCriticalConfirm();
  const cur = snapshot?.watchdog || {};
  // Sprint 4.5.1 — dual target. Fallback: snapshot legacy só tem 'target' → vira host1
  const initHost1 = cur.host1 || cur.target || "8.8.8.8";
  const initHost2 = cur.host2 || "1.1.1.1";

  const [enabled, setEnabled] = useState(cur.enabled !== false);
  const [host1, setHost1] = useState(initHost1);
  const [host2, setHost2] = useState(initHost2);
  const [mode, setMode] = useState(cur.mode || "ALL_FAIL");
  const [action, setAction] = useState(cur.action || "relay_reboot");
  const [intervalS, setIntervalS] = useState(cur.interval || 60);
  const [timeoutS, setTimeoutS] = useState(cur.timeout || 5);
  const [rebootAfter, setRebootAfter] = useState(cur.reboot_failures || 3);
  const [cooldownS, setCooldownS] = useState(cur.cooldown || 300);

  useEffect(() => {
    if (!snapshot?.watchdog) return;
    const w = snapshot.watchdog;
    setEnabled(w.enabled === true);
    if (w.host1 || w.target) setHost1(w.host1 || w.target);
    if (w.host2 !== undefined) setHost2(w.host2 || "");
    if (w.mode)    setMode(w.mode);
    if (w.action)  setAction(w.action);
    if (w.interval) setIntervalS(w.interval);
    if (w.timeout)  setTimeoutS(w.timeout);
    if (w.reboot_failures) setRebootAfter(w.reboot_failures);
    if (w.cooldown) setCooldownS(w.cooldown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.watchdog?.enabled, snapshot?.watchdog?.host1, snapshot?.watchdog?.host2, snapshot?.watchdog?.mode]);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);

  const submit = async () => {
    if (enabled && !host1.trim()) {
      setErr("Host primário obrigatório quando habilitado.");
      return;
    }
    const secret = await confirmCritical({
      actionLabel: "Aplicar configuração de watchdog",
      target: { kind: "single", deviceId },
      impact:
        "O watchdog pode REINICIAR o relé/ESP automaticamente conforme a regra configurada.",
    });
    if (secret == null) return;
    setBusy(true);
    setErr(null);
    setOk(false);
    try {
      const body = { enabled };
      if (enabled) {
        body.host1 = host1.trim();
        if (host2.trim()) body.host2 = host2.trim();
        body.mode = mode;
        body.action = action;
        body.interval_s = Number(intervalS);
        body.timeout_s = Number(timeoutS);
        body.reboot_after_fails = Number(rebootAfter);
        body.cooldown_s = Number(cooldownS);
      }
      await configWatchdog(deviceId, body, { confirm: secret });
      setOk(true);
      onApplied?.();
      setTimeout(() => setOk(false), 5000);
    } catch (e) {
      setErr(e?.message || "Erro ao aplicar watchdog");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span className="font-semibold">Habilitar watchdog</span>
      </label>
      {cur.enabled !== undefined && cur.enabled !== enabled && (
        <div className="text-[10px] text-noc-accent font-mono ml-6">
          Atual: <span className="text-noc-muted">{cur.enabled ? "ON" : "OFF"}</span>
          <span className="mx-1">→</span>
          <span className="text-noc-accent">{enabled ? "ON" : "OFF"}</span>
        </div>
      )}

      <div className="text-[10px] text-noc-muted/70">
        Watchdog dual-target: device pinga 2 hosts em intervalos regulares.
        Modo determina quando disparar a ação.
      </div>

      {enabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Host primário</span>
              <input value={host1} onChange={(e) => setHost1(e.target.value)}
                placeholder="8.8.8.8"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.host1 || cur.target} next={host1} />
            </label>
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Host secundário (opcional)</span>
              <input value={host2} onChange={(e) => setHost2(e.target.value)}
                placeholder="1.1.1.1 (vazio = single host)"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.host2} next={host2} />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Modo de decisão</span>
              <select value={mode} onChange={(e) => setMode(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono">
                <option value="ALL_FAIL">ALL_FAIL — dispara se AMBOS falharem</option>
                <option value="ANY_FAIL">ANY_FAIL — dispara se QUALQUER falhar</option>
              </select>
              <DiffHint current={cur.mode} next={mode} />
            </label>
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Ação ao disparar</span>
              <select value={action} onChange={(e) => setAction(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono">
                <option value="relay_reboot">relay_reboot — desliga e religa relé</option>
                <option value="esp_reboot">esp_reboot — reinicia ESP32</option>
                <option value="alert_only">alert_only — apenas registra</option>
              </select>
              <DiffHint current={cur.action} next={action} />
            </label>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Intervalo (s)</span>
              <input type="number" value={intervalS}
                min={10} max={3600}
                onChange={(e) => setIntervalS(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.interval} next={Number(intervalS)} />
            </label>
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Timeout (s)</span>
              <input type="number" value={timeoutS}
                min={1} max={60}
                onChange={(e) => setTimeoutS(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.timeout} next={Number(timeoutS)} />
            </label>
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Falhas</span>
              <input type="number" value={rebootAfter}
                min={1} max={100}
                onChange={(e) => setRebootAfter(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.reboot_failures} next={Number(rebootAfter)} />
            </label>
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Cooldown (s)</span>
              <input type="number" value={cooldownS}
                min={10} max={86400}
                onChange={(e) => setCooldownS(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
              <DiffHint current={cur.cooldown} next={Number(cooldownS)} />
            </label>
          </div>

          <div className="text-[10px] text-noc-muted/60">
            {host2.trim()
              ? `Modo ${mode}: ping ${host1} ${mode === "ALL_FAIL" ? "E" : "OU"} ${host2} a cada ${intervalS}s.`
              : `Single-host: ping apenas ${host1} a cada ${intervalS}s.`}
            {" "}Reboot após {rebootAfter} falhas (action = {action}). Cooldown {cooldownS}s.
          </div>

          {cur.last_reason && (
            <div className="text-[10px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded px-2 py-1.5 font-mono">
              Última ocorrência: {cur.last_reason}
            </div>
          )}
        </>
      )}

      <ErrorBox err={err} onDismiss={() => setErr(null)} />
      <SuccessBadge shown={ok} />

      <div className="flex justify-end">
        <ApplyButton busy={busy} onClick={submit} />
      </div>
    </div>
  );
}


// ============================================================================
// ABA AGENDA
// ============================================================================

// Sprint 4.7.1 — DAYS constante movida para ScheduleEditor.DAY_LABELS/DAY_NAMES

function ScheduleTab({ deviceId, snapshot, onApplied }) {
  // S3.10B fix — o hook DEVE estar AQUI (ScheduleTab usa confirmCritical no
  // submit). Antes estava no DeviceConfigModal (componente irmão) → fora de
  // escopo → ReferenceError em runtime: não pedia senha nem criava a agenda.
  const confirmCritical = useCriticalConfirm();
  const cur = snapshot?.schedule || {};

  // A2.1 — agenda REAL do scheduler v1 (firmware, source="firmware_v1") é
  // tratada como INFORMAÇÃO somente-leitura (card no topo) e NÃO entra no
  // editor. Para um device só-v1 o editor começa VAZIO (como antes para um
  // device sem agenda cloud). O editor abaixo segue normal (config cloud).
  const _DOW = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const realV1Entries = (cur.entries || []).filter((e) => e?.source === "firmware_v1");
  const isV1ReadOnly = realV1Entries.length > 0;
  // S2.9 — agenda real ATIVA resolvida do snapshot (v1 OU v2), MESMA fonte da
  // aba Automação (resolveActiveSchedule). Usada SOMENTE no card read-only
  // abaixo; NÃO altera o editor (config cloud), o write path nem a validação.
  const _active = resolveActiveSchedule(snapshot, null);
  const _activeEntries = _active.active.entries;
  const _activeSource = _active.activeSource;

  // Sprint 4.7.1 — Editor unificado: usamos o mesmo modelo do TemplateBuilder.
  // O estado interno é { enabled, entries: [...], exceptions: [...] }.
  const [data, setData] = useState(() =>
    isV1ReadOnly
      ? { enabled: false, entries: [], exceptions: [] }
      : {
          enabled: cur.enabled === true,
          entries: Array.isArray(cur.entries) ? cur.entries.map((e) => ({ ...e })) : [],
          exceptions: Array.isArray(cur.exceptions) ? [...cur.exceptions] : [],
        },
  );

  // Re-sync quando snapshot chega/muda
  useEffect(() => {
    if (!snapshot?.schedule) return;
    // A2.1 — agenda v1 (firmware) é info-only: editor permanece vazio.
    const v1 = (snapshot.schedule.entries || []).some((e) => e?.source === "firmware_v1");
    if (v1) {
      setData({ enabled: false, entries: [], exceptions: [] });
      return;
    }
    setData({
      enabled: snapshot.schedule.enabled === true,
      entries: Array.isArray(snapshot.schedule.entries)
        ? snapshot.schedule.entries.map((e) => ({ ...e }))
        : [],
      exceptions: Array.isArray(snapshot.schedule.exceptions)
        ? [...snapshot.schedule.exceptions]
        : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.schedule?.enabled, snapshot?.schedule?.entries?.length]);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);
  // Sprint 4.7.2 — preview drawer
  const [previewOpen, setPreviewOpen] = useState(false);
  // Sprint 4.7.3 — modal de copiar agenda de outro device
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [copiedToast, setCopiedToast] = useState(null);

  // Patch parcial vindo do ScheduleEditor → merge no estado
  const onEditorChange = (patch) => {
    setData((prev) => ({ ...prev, ...patch }));
  };

  // Sprint 4.7.3 — handler chamado pelo ScheduleSourcePicker quando
  // o operador confirma a cópia. Apenas preenche o editor local —
  // NÃO envia ao backend (briefing exige clicar Aplicar depois).
  const handlePickSource = (sourceSchedule, sourceDevice) => {
    setData({
      enabled: sourceSchedule.enabled === true,
      entries: Array.isArray(sourceSchedule.entries)
        ? sourceSchedule.entries.map((e) => ({ ...e }))
        : [],
      exceptions: Array.isArray(sourceSchedule.exceptions)
        ? [...sourceSchedule.exceptions]
        : [],
    });
    setSourcePickerOpen(false);
    // Toast informativo (5s)
    setCopiedToast(
      sourceDevice?.device_id
        ? `${t("scheduleSource.copiedToast")} (${sourceDevice.device_id})`
        : t("scheduleSource.copiedToast")
    );
    setTimeout(() => setCopiedToast(null), 5000);
  };

  // Sprint 4.7.4 — handler chamado pelo SchedulePresetsBox.
  // Preset já vem clonado pelo box (clonePresetSchedule).
  // Mesma regra: preenche editor + toast, NÃO aplica automaticamente.
  const handlePickPreset = (presetSchedule, preset) => {
    setData({
      enabled: presetSchedule.enabled === true,
      entries: Array.isArray(presetSchedule.entries)
        ? presetSchedule.entries.map((e) => ({ ...e }))
        : [],
      exceptions: Array.isArray(presetSchedule.exceptions)
        ? [...presetSchedule.exceptions]
        : [],
    });
    const name = preset?.nameKey ? t(preset.nameKey) : "?";
    setCopiedToast(t("schedulePresets.appliedToast", { name }));
    setTimeout(() => setCopiedToast(null), 5000);
  };

  // Sprint 4.7.2 — operações sobre o estado do editor.
  // O snapshot serve como "ponto de verdade" lido do device — vem do
  // backend via getDeviceConfigSnapshot e é passado por prop.
  const hasSnapshot = !!snapshot?.schedule;

  // Copia a agenda atual (lida do device) para o editor. Mesma coisa que
  // "Restaurar atual" — mantido com nome separado por clareza no briefing.
  const copyOrRestoreCurrent = () => {
    if (!snapshot?.schedule) return;
    setData({
      enabled: snapshot.schedule.enabled === true,
      entries: Array.isArray(snapshot.schedule.entries)
        ? snapshot.schedule.entries.map((e) => ({ ...e }))
        : [],
      exceptions: Array.isArray(snapshot.schedule.exceptions)
        ? [...snapshot.schedule.exceptions]
        : [],
    });
  };

  const clearAll = () => {
    if (!window.confirm(t("schedulePreview.confirmClearAll"))) return;
    setData({ enabled: false, entries: [], exceptions: [] });
  };

  const submit = async () => {
    // Validação client-side (espelha backend ScheduleEntryIn._validate_mode).
    // Backend rejeitará igualmente se passar; aqui evitamos o round-trip.
    const vErr = validateSchedule(data);
    if (vErr) {
      setErr(vErr);
      return;
    }
    // S2.1 — regra v1/v2 ÚNICA (scheduleCore), mesma da Aba Automação.
    const cap = getSchedulerCapability(snapshot);
    const vCap = validateScheduleForCapability(data.entries, cap);
    if (!vCap.ok) {
      setErr(vCap.reason);
      return;
    }
    // S2.1 — builder ÚNICO do payload (sanitiza entries + exceptions + enabled).
    const payload = buildConfigSchedulePayload({
      entries: data.entries,
      exceptions: data.exceptions,
      enabled: data.enabled,
    });
    // S3.10B — agenda com ação crítica programada no device exige confirmação.
    // Checa o payload NORMALIZADO (exatamente o que o backend recebe/valida).
    const _CRIT = new Set(["relay_off", "relay_reboot", "esp_reboot"]);
    let confirm;
    if ((payload.entries || []).some((e) => _CRIT.has(e.action))) {
      const secret = await confirmCritical({
        actionLabel: "Enviar agenda ao device",
        target: { kind: "single", deviceId },
        impact: "Esta ação vai PROGRAMAR o dispositivo físico (desligar/reiniciar relé ou ESP).",
      });
      if (secret == null) return;
      confirm = secret;
    }
    setBusy(true);
    setErr(null);
    setOk(false);
    try {
      await configSchedule(deviceId, payload, { confirm });
      setOk(true);
      onApplied?.();
      setTimeout(() => setOk(false), 5000);
    } catch (e) {
      setErr(e?.message || t("schedule.errApply"));
    } finally {
      setBusy(false);
    }
  };

  // Snapshot da agenda atual (lida do device) — usado pelo preview
  // como referência. Se snapshot ainda não chegou, mostramos como
  // "vazio" no Atual e tudo na proposta será "added".
  const currentSchedule = hasSnapshot
    ? {
        enabled: snapshot.schedule.enabled === true,
        entries: Array.isArray(snapshot.schedule.entries)
          ? snapshot.schedule.entries
          : [],
        exceptions: Array.isArray(snapshot.schedule.exceptions)
          ? snapshot.schedule.exceptions
          : [],
      }
    : { enabled: false, entries: [], exceptions: [] };

  return (
    <div className="space-y-3">
      {/* S2.9 — Card da agenda REAL ATIVA do dispositivo (v1 OU v2 resolvido do
          snapshot, igual à aba Automação). SOMENTE LEITURA, no topo. O editor
          do NOC (config cloud) continua normal abaixo, inalterado. */}
      {_activeEntries.length > 0 && (
        <div className="space-y-1 border border-noc-warn/30 bg-noc-warn/10 rounded p-2">
          <div className="text-[11px] text-noc-warn flex items-center gap-1.5">
            <span>🔒</span>
            <span>Agenda real do dispositivo (scheduler local Sonoff)</span>
            <span className="ipwd-exec-source">Execução: {_activeSource}</span>
          </div>
          {_activeEntries.map((e, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-2 text-xs bg-noc-bg/40 border border-noc-border/60 rounded px-2 py-1"
            >
              <span className="font-mono text-noc-accent">{e.time || "--:--"}</span>
              <span className="text-noc-muted">
                {(e.days || []).map((d) => _DOW[d]).join(", ") || "—"}
              </span>
              <span className="text-noc-muted">
                {e.duration_s != null ? `${e.duration_s}s` : "—"}
              </span>
              <span className="font-mono text-noc-warn">
                {e.action || "relay_reboot"}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-noc-muted/70">
        {t("schedule.scopeNote")}
      </div>

      {/* S2.6/S2.9 — Agenda V2 persistida. Sob execução v2, o card "Agenda real"
          acima já a exibe → este card só aparece quando a fonte ativa NÃO é v2
          (ex.: v2 dormente sob scheduler v1), evitando duplicação visual. */}
      {_activeSource !== "v2" && <ScheduleV2Card snapshot={snapshot} />}

      {/* Sprint 4.7.4 — Presets prontos (colapsado por padrão) */}
      <SchedulePresetsBox
        currentSchedule={currentSchedule}
        onApply={handlePickPreset}
      />

      {/* Sprint 4.7.1 — Editor compartilhado (mesmo do TemplateBuilder) */}
      <ScheduleEditor
        data={data}
        onChange={onEditorChange}
        showExceptions
        // ACT-2-NOC — liga/desliga discreto só com scheduler v2 no device
        allowSwitchActions={getSchedulerCapability(snapshot).multi_event === true}
      />

      <ErrorBox err={err} onDismiss={() => setErr(null)} />
      <SuccessBadge shown={ok} />

      {/* Sprint 4.7.3 — Toast informativo após copiar agenda de outro device */}
      {copiedToast && (
        <div className="text-[11px] text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2">
          ✓ {copiedToast}
        </div>
      )}

      {/* Sprint 4.7.2 + 4.7.3 — Botões de operação + Preview */}
      <div className="flex flex-wrap items-center gap-2 justify-end">
        {/* Sprint 4.7.3 — Copiar de outro device */}
        <button
          type="button"
          onClick={() => setSourcePickerOpen(true)}
          className="text-[11px] px-2 py-1 rounded border border-noc-purple/30 text-noc-purple hover:bg-noc-purple/10"
          title={t("scheduleSource.title")}
        >
          {t("scheduleSource.btnOpen")}
        </button>
        <button
          type="button"
          onClick={copyOrRestoreCurrent}
          disabled={!hasSnapshot}
          className="text-[11px] px-2 py-1 rounded border border-noc-accent/30 text-noc-accent hover:bg-noc-accent/10 disabled:opacity-40 disabled:cursor-not-allowed"
          title={t("schedulePreview.copyCurrentHelp")}
        >
          📋 {t("schedulePreview.copyCurrent")}
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="text-[11px] px-2 py-1 rounded border border-noc-err/30 text-noc-err hover:bg-noc-err/10"
          title={t("schedulePreview.clearAllHelp")}
        >
          🗑 {t("schedulePreview.clearAll")}
        </button>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          disabled={!hasSnapshot}
          className="text-[11px] px-2 py-1 rounded border border-noc-ok/30 text-noc-ok hover:bg-noc-ok/10 disabled:opacity-40 disabled:cursor-not-allowed"
          title={hasSnapshot ? "" : t("schedulePreview.btnPreviewDisabled")}
        >
          {t("schedulePreview.btnPreview")}
        </button>
        <ApplyButton busy={busy} onClick={submit} />
      </div>

      {/* Sprint 4.7 — Histórico de execuções da agenda (firmware 2.7.3+) */}
      <ScheduleHistoryBox deviceId={deviceId} />

      {/* Sprint 4.7.2 — Preview drawer */}
      <SchedulePreviewModal
        open={previewOpen}
        current={currentSchedule}
        proposed={data}
        onClose={() => setPreviewOpen(false)}
        onCopyCurrent={hasSnapshot ? copyOrRestoreCurrent : null}
        onRestoreCurrent={hasSnapshot ? copyOrRestoreCurrent : null}
        onClearAll={clearAll}
      />

      {/* Sprint 4.7.3 — Copiar agenda de outro device */}
      <ScheduleSourcePicker
        open={sourcePickerOpen}
        excludeDeviceId={deviceId}
        onClose={() => setSourcePickerOpen(false)}
        onPick={handlePickSource}
      />
    </div>
  );
}


// ============================================================================
// Sprint 4.7 — Histórico de execuções (lê /devices/{id}/schedule-events)
// ============================================================================

function ScheduleHistoryBox({ deviceId }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [limit] = useState(20);

  const load = async () => {
    if (!deviceId) return;
    setLoading(true); setErr(null);
    try {
      const r = await listScheduleEvents(deviceId, { limit });
      setEvents(r.events || []);
      setTotal(r.total || 0);
    } catch (e) {
      setErr(e?.message || "Erro ao carregar histórico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [deviceId]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const fmtDuration = (s) => {
    if (s == null) return "—";
    if (s === 0) return "0s";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r ? `${m}m${r}s` : `${m}m`;
  };

  const resultBadge = (r) => {
    const cls = {
      ok:      "border-noc-ok/30 text-noc-ok bg-noc-ok/10",
      fail:    "border-noc-err/30 text-noc-err bg-noc-err/10",
      busy:    "border-noc-warn/30 text-noc-warn bg-noc-warn/10",
      skipped: "border-noc-border text-noc-muted bg-noc-bg/40",
    }[r] || "border-noc-border text-noc-muted";
    const label = (t(`schedule.runStatus.${r}`) || r || "—").toUpperCase();
    return (
      <span className={`text-[9px] font-bold px-1 py-0.5 rounded border ${cls}`}>
        {label}
      </span>
    );
  };

  return (
    <fieldset className="border border-noc-accent/30 rounded p-3 mt-3">
      <legend className="text-xs text-noc-accent px-1 flex items-center gap-2">
        📜 {t("schedule.history")}
        <span className="text-noc-muted/70 font-normal">
          ({t("schedule.historyEventCount", { n: total })})
        </span>
        <button type="button" onClick={load} disabled={loading}
          className="ml-auto text-[10px] text-noc-muted hover:text-noc-accent disabled:opacity-50">
          {loading ? t("common.loading") : `↻ ${t("common.reload").toLowerCase()}`}
        </button>
      </legend>

      {err && (
        <div className="text-[11px] text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 mb-2">
          {err}
        </div>
      )}

      {!err && events.length === 0 && !loading && (
        <div className="text-[11px] text-noc-muted/70 italic py-1">
          {t("schedule.noEvents")}
          <span className="text-noc-muted/50 ml-1">
            {t("schedule.noEventsHint")}
          </span>
        </div>
      )}

      {events.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-noc-muted/80 uppercase tracking-wide border-b border-noc-border/40">
                <th className="text-left px-1 py-1">{t("schedule.when")}</th>
                <th className="text-left px-1 py-1">{t("schedule.entry")}</th>
                <th className="text-left px-1 py-1">{t("schedule.time")}</th>
                <th className="text-left px-1 py-1">{t("schedule.action")}</th>
                <th className="text-left px-1 py-1">{t("schedule.result")}</th>
                <th className="text-right px-1 py-1">{t("schedule.duration")}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-b border-noc-border/20 hover:bg-noc-bg/30">
                  <td className="px-1 py-1 font-mono text-noc-accent">{fmtDate(e.executed_at)}</td>
                  <td className="px-1 py-1 text-noc-muted">#{e.entry_idx}</td>
                  <td className="px-1 py-1 font-mono text-noc-text">{e.scheduled_time}</td>
                  <td className="px-1 py-1 text-noc-text">{e.action}</td>
                  <td className="px-1 py-1">{resultBadge(e.result)}</td>
                  <td className="px-1 py-1 text-right font-mono text-noc-muted">{fmtDuration(e.duration_s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > events.length && (
            <div className="text-[10px] text-noc-muted/60 mt-1 italic">
              {t("schedule.showingEvents", { n: events.length, total })}
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}


// ============================================================================
// ABA AVANÇADO (placeholder)
// ============================================================================

// ============================================================================
// ABA AVANÇADO — Sprint 4.5
//
// Editável. Lê config_snapshot.advanced (Sprint 4.4.2 já entrega) e
// permite editar 6 campos via config_advanced (novo endpoint Sprint 4.5).
//
// Heartbeat interval: number 30-600s
// RSSI threshold:     slider -95 a -30 dBm
// Relay default boot: select restore/on/off
// OTA auto:           switch
// Safe mode:          switch
// Auto reboot:        number 0-365 dias (0 = desabilitado)
// ============================================================================

function AdvancedTab({ deviceId, snapshot, snapState, snapSyncedAt, snapRevision, onApplied }) {
  // S3.10C1 — confirmação forte de config crítica (hook no escopo do tab).
  const confirmCritical = useCriticalConfirm();
  const cur = snapshot?.advanced || {};

  // Estado inicial — usa snapshot ou defaults
  const [heartbeatInterval, setHeartbeatInterval] = useState(cur.heartbeat_interval ?? 60);
  const [rssiThreshold, setRssiThreshold]         = useState(cur.rssi_threshold ?? -75);
  const [relayBoot, setRelayBoot]                 = useState(cur.relay_boot ?? "restore");
  const [otaAuto, setOtaAuto]                     = useState(cur.ota_auto ?? false);
  const [safeMode, setSafeMode]                   = useState(cur.safe_mode ?? false);
  const [autoReboot, setAutoReboot]               = useState(cur.auto_reboot ?? 0);

  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const [ok, setOk]     = useState(false);

  // Re-sync quando snapshot chega depois de o tab montar
  useEffect(() => {
    if (!snapshot?.advanced) return;
    const a = snapshot.advanced;
    if (a.heartbeat_interval !== undefined) setHeartbeatInterval(a.heartbeat_interval);
    if (a.rssi_threshold     !== undefined) setRssiThreshold(a.rssi_threshold);
    if (a.relay_boot         !== undefined) setRelayBoot(a.relay_boot);
    if (a.ota_auto           !== undefined) setOtaAuto(a.ota_auto);
    if (a.safe_mode          !== undefined) setSafeMode(a.safe_mode);
    if (a.auto_reboot        !== undefined) setAutoReboot(a.auto_reboot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.advanced?.heartbeat_interval, snapshot?.advanced?.relay_boot]);

  const submit = async () => {
    // Monta payload com TODOS os campos (todos têm valor inicial; é safe enviar)
    const body = {
      heartbeat_interval: Number(heartbeatInterval),
      rssi_threshold: Number(rssiThreshold),
      relay_boot: relayBoot,
      ota_auto: !!otaAuto,
      safe_mode: !!safeMode,
      auto_reboot: Number(autoReboot),
    };
    const secret = await confirmCritical({
      actionLabel: "Aplicar configuração avançada",
      target: { kind: "single", deviceId },
      impact:
        "Pode alterar relay_boot (estado do relé no boot), auto-OTA e parâmetros de runtime.",
    });
    if (secret == null) return;
    setBusy(true);
    setErr(null);
    setOk(false);
    try {
      await configAdvanced(deviceId, body, { confirm: secret });
      setOk(true);
      onApplied?.();
      setTimeout(() => setOk(false), 5000);
    } catch (e) {
      // Sprint 4.6.2 — api.js já formata e.message com loc+msg legíveis.
      // Mas se ainda chegou "invalid_payload" cru (caller antigo), troca por
      // texto amigável.
      let msg = e?.message || "Erro ao aplicar config avançada";
      if (msg === "invalid_payload" || /^invalid_payload$/i.test(msg.trim())) {
        msg = "Payload inválido. Verifique os valores (heartbeat 30-600s, " +
              "RSSI -95..-30 dBm, auto-reboot 0-365 dias).";
      }
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header sync info */}
      <div className="bg-noc-bg/50 border border-noc-border rounded p-2 flex items-center justify-between text-[10px]">
        <div className="flex items-center gap-3">
          <span className="text-noc-muted">Estado:</span>
          <span className={`font-bold px-2 py-0.5 rounded border ${
            STATE_BADGES[snapState]?.cls || STATE_BADGES.unknown.cls
          }`}>
            {STATE_BADGES[snapState]?.label || "UNKNOWN"}
          </span>
          {snapRevision !== null && snapRevision !== undefined && (
            <span className="text-noc-muted/80 font-mono">rev {snapRevision}</span>
          )}
        </div>
        {snapSyncedAt && (
          <span className="text-noc-muted/70">último sync: {fmtRel(snapSyncedAt)}</span>
        )}
      </div>

      <div className="text-[10px] text-noc-muted/70">
        Configurações de runtime do firmware. Aplicação imediata (sem reboot).
        Persistido em NVS.
      </div>

      {/* Grid 2 colunas para os 6 campos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

        {/* Heartbeat interval (number) */}
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Heartbeat interval (segundos)</span>
          <input type="number" value={heartbeatInterval}
            min={30} max={600}
            onChange={(e) => setHeartbeatInterval(e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
          <DiffHint current={cur.heartbeat_interval} next={Number(heartbeatInterval)} />
          <span className="text-[10px] text-noc-muted/60">30..600 segundos</span>
        </label>

        {/* RSSI threshold (slider) */}
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">RSSI threshold ({rssiThreshold} dBm)</span>
          <input type="range" value={rssiThreshold}
            min={-95} max={-30} step={1}
            onChange={(e) => setRssiThreshold(Number(e.target.value))}
            className="w-full" />
          <DiffHint current={cur.rssi_threshold} next={Number(rssiThreshold)} format={(v) => `${v} dBm`} />
          <span className="text-[10px] text-noc-muted/60">Sinal abaixo deste valor = "weak"</span>
        </label>

        {/* Relay default boot (select) */}
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Relé default boot</span>
          <select value={relayBoot}
            onChange={(e) => setRelayBoot(e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono">
            <option value="restore">restore (último estado)</option>
            <option value="on">on (sempre ligado)</option>
            <option value="off">off (sempre desligado)</option>
          </select>
          <DiffHint current={cur.relay_boot} next={relayBoot} />
        </label>

        {/* Auto reboot (number) */}
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Auto reboot (dias)</span>
          <input type="number" value={autoReboot}
            min={0} max={365}
            onChange={(e) => setAutoReboot(e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
          <DiffHint current={cur.auto_reboot} next={Number(autoReboot)} format={(v) => v === 0 ? "desabilitado" : `${v} dias`} />
          <span className="text-[10px] text-noc-muted/60">0 = desabilitado</span>
        </label>

        {/* OTA auto (switch) */}
        <div className="text-xs space-y-1">
          <span className="text-noc-muted">OTA auto</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOtaAuto(true)}
              className={`flex-1 px-2 py-1 rounded text-[10px] border ${otaAuto ? "border-noc-ok/30 bg-noc-ok/10 text-noc-ok" : "border-noc-border text-noc-muted"}`}>
              ON
            </button>
            <button type="button" onClick={() => setOtaAuto(false)}
              className={`flex-1 px-2 py-1 rounded text-[10px] border ${!otaAuto ? "border-noc-err/30 bg-noc-err/10 text-noc-err" : "border-noc-border text-noc-muted"}`}>
              OFF
            </button>
          </div>
          {cur.ota_auto !== undefined && cur.ota_auto !== otaAuto && (
            <div className="text-[10px] text-noc-accent font-mono">
              Atual: <span className="text-noc-muted">{cur.ota_auto ? "ON" : "OFF"}</span>
              <span className="mx-1">→</span>
              <span className="text-noc-accent">{otaAuto ? "ON" : "OFF"}</span>
            </div>
          )}
        </div>

        {/* Safe mode (switch) */}
        <div className="text-xs space-y-1">
          <span className="text-noc-muted">Safe mode</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setSafeMode(true)}
              className={`flex-1 px-2 py-1 rounded text-[10px] border ${safeMode ? "border-noc-warn/30 bg-noc-warn/10 text-noc-warn" : "border-noc-border text-noc-muted"}`}>
              ON
            </button>
            <button type="button" onClick={() => setSafeMode(false)}
              className={`flex-1 px-2 py-1 rounded text-[10px] border ${!safeMode ? "border-noc-border bg-noc-bg/50 text-noc-text" : "border-noc-border text-noc-muted"}`}>
              OFF
            </button>
          </div>
          {cur.safe_mode !== undefined && cur.safe_mode !== safeMode && (
            <div className="text-[10px] text-noc-accent font-mono">
              Atual: <span className="text-noc-muted">{cur.safe_mode ? "ON" : "OFF"}</span>
              <span className="mx-1">→</span>
              <span className="text-noc-accent">{safeMode ? "ON" : "OFF"}</span>
            </div>
          )}
          {safeMode && (
            <div className="text-[10px] text-noc-warn">
              ⚠ Desabilita execução automática de scripts/agenda
            </div>
          )}
        </div>

      </div>

      {/* Estado do runtime (read-only, derivado de outras seções do snapshot) */}
      {snapshot && (
        <div className="border-t border-noc-border pt-3 mt-3 space-y-2">
          <div className="text-[10px] text-noc-muted/70 uppercase tracking-wide">
            Estado do runtime (read-only)
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <div className="bg-noc-bg/40 border border-noc-border/40 rounded px-2 py-1.5">
              <dt className="text-[10px] text-noc-muted/80 uppercase tracking-wide">Watchdog</dt>
              <dd className={`font-mono mt-0.5 ${snapshot.watchdog?.enabled ? "text-noc-ok" : "text-noc-muted"}`}>
                {snapshot.watchdog?.enabled ? "ON" : "OFF"}
              </dd>
            </div>
            <div className="bg-noc-bg/40 border border-noc-border/40 rounded px-2 py-1.5">
              <dt className="text-[10px] text-noc-muted/80 uppercase tracking-wide">Schedule</dt>
              {/* S2.9/B8 — enabled do scheduler ATIVO (v1 OU v2), não só o bloco v1. */}
              <dd className={`font-mono mt-0.5 ${resolveActiveSchedule(snapshot, null).active.enabled ? "text-noc-ok" : "text-noc-muted"}`}>
                {resolveActiveSchedule(snapshot, null).active.enabled ? "ON" : "OFF"}
              </dd>
            </div>
            {snapshot.wifi?.ssid && (
              <div className="col-span-2 bg-noc-bg/40 border border-noc-border/40 rounded px-2 py-1.5">
                <dt className="text-[10px] text-noc-muted/80 uppercase tracking-wide">Wi-Fi SSID conectado</dt>
                <dd className="font-mono mt-0.5 text-noc-accent">
                  {snapshot.wifi.ssid}
                  <span className="text-noc-muted/60 ml-2">
                    ({snapshot.wifi.dhcp ? "DHCP" : "estático"})
                  </span>
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      <ErrorBox err={err} onDismiss={() => setErr(null)} />
      <SuccessBadge shown={ok} />

      <div className="flex justify-end">
        <ApplyButton busy={busy} onClick={submit} label="Aplicar Avançado" />
      </div>
    </div>
  );
}
